<script setup>
// Astra DS · ModuleTitleBar — la cabecera de módulo.
//
// Figma: Pattern/ModuleTitleBar-Reset 655:525.
//
// «End=action (chip), live (chip rojo con punto) o index (dato al vuelo).»
//
// Es la hermana pequeña de PageTitleBar: misma composición —título a la
// izquierda, final a la derecha, regla debajo— con `display/section` en vez de
// `display/page` y sin cuadrado portadora, que cierra páginas y no módulos.
//
// Reescrita el 25.08.26 contra el nodo del reset. La versión anterior venía de
// `326:113`, del diseño que se jubila: título a 32 en vez de 38, padding
// 24/32 en vez de 16/14/28, regla `default` en vez de `subtle`, y sin el final
// `index`.
import ChipAction from "./ChipAction.vue";
import { i18n } from "@/i18n";

defineProps({
  title: { type: String, required: true },

  end: {
    type: String,
    default: "none",
    validator: (v) => ["action", "live", "index", "none"].includes(v),
  },

  // end="action"
  actionText: { type: String, default: () => i18n.global.t("piezas.titulos.verTodo") },
  actionTo: { type: [String, Object], default: null },
  actionHref: { type: String, default: "" },

  // end="live"
  liveText: { type: String, default: () => i18n.global.t("piezas.titulos.irARadio") },
  liveTo: { type: [String, Object], default: null },

  // end="index"
  index: { type: String, default: "" },
});
</script>

<template>
  <div class="module-title-bar">
    <p class="module-title-bar__title">{{ title }}</p>

    <ChipAction
      v-if="end === 'live'"
      :label="liveText"
      variant="solid-accent"
      show-dot
      :to="liveTo"
    />

    <ChipAction
      v-else-if="end === 'action'"
      :label="actionText"
      variant="outlined"
      :to="actionTo"
      :href="actionHref"
    />

    <span v-else-if="end === 'index'" class="module-title-bar__index">{{
      index
    }}</span>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

// Alineación por línea base, no por centro: el chip se apoya en la base del
// título. Centrarlo contra un titular de 38 lo deja flotando.
.module-title-bar {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  box-sizing: border-box;
  width: 100%;
  padding: 16px 28px 14px;
  background: var(--color-bg-canvas);
  // Dos reglas distintas y no es capricho: `line-strong` arriba separa este
  // módulo del anterior, `line-subtle` abajo separa el título de su contenido.
  // Es la gramática del sistema — la línea fuerte separa módulos, la fina
  // separa dentro de uno. El borde lo lleva la sección, no lo pone la vista.
  // 🔴 Aquí había un `border-top` en `border/strong`. En oscuro pasaba por
  // hairline —`neutral/700`—, pero en claro `strong` es `neutral/900`: una línea
  // casi negra encima de cada sección. Y el nodo `655:525` no lleva filete
  // arriba, solo abajo. DESIGN.md § Colors: «los hairlines tienen contraste bajo
  // a propósito: son estructura, no boundary crítico».
  border-bottom: 1px solid var(--color-border-default);
  overflow: hidden;
}

.module-title-bar__title {
  @include t.display-section;
  flex: 1;
  min-width: 0;
  margin: 0;
  color: var(--color-text-primary);
}

// 🔴 **`mono/micro` sobre `text/tertiary`, como el maestro `655:525`.** Del
// 31.08.26 al 30.09.26 fue `mono/para` 11 sobre secundario, el arreglo del área
// de cuenta llevado al componente; nunca pasó a Figma, y con su línea de 21 la
// barra medía 66 en vez de 62. Pablo eligió el 30.09.26 volver al maestro, para
// todas las barras.
.module-title-bar__index {
  @include t.mono-micro;
  // Alineado por la línea base contra un titular de 34, la línea de 1,4 del
  // dato colgaba un píxel por debajo del título y la barra medía 63. Es una
  // sola línea: no necesita interlineado.
  line-height: 1;
  color: var(--color-text-tertiary);
  white-space: nowrap;
}

@media (max-width: 880px) {
  .module-title-bar {
    padding: 14px 20px;
  }
}
</style>
